import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Analytics } from "@/components/Analytics";
import "@/App.css";
import { I18nProvider, useI18n } from "@/i18n/I18nContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import HomePage from "@/pages/Home";
import BellagioPage from "@/pages/Bellagio";
import RoomsPage from "@/pages/Rooms";
import AboutPage from "@/pages/About";
import LocationPage from "@/pages/Location";
import ReviewsPage from "@/pages/Reviews";
import BookingPage from "@/pages/Booking";
import ServicesPage from "@/pages/Services";
import PrivacyPage from "@/pages/Privacy";
import FAQPage from "@/pages/FAQ";
import AdminPage from "@/pages/Admin";
import BlogPage from "@/pages/Blog";
import BlogPostPage from "@/pages/BlogPost";
import BreakfastPage from "@/pages/Breakfast";
import { SEO } from "@/components/SEO";
import { FloatingContact } from "@/components/FloatingContact";
import { CookieBanner } from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";

const SUPPORTED = ["it", "en", "fr"];

// Hard redirect the legacy IT subdomain to the canonical www domain.
// Only fires on the real legacy host; never affects preview/localhost.
const HostRedirect = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hostname === "it.ilakeitbellagio.it") {
      const p = window.location.pathname;
      const dest = p && p !== "/" ? p : "/it";
      window.location.replace("https://www.ilakeitbellagio.it" + dest + window.location.search + window.location.hash);
    }
  }, []);
  return null;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) { setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80); return; }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

// Sync URL lang param with I18n context
const LangSync = () => {
  const { lang: urlLang } = useParams();
  const { lang, setLang } = useI18n();
  useEffect(() => {
    if (urlLang && SUPPORTED.includes(urlLang) && urlLang !== lang) setLang(urlLang);
  }, [urlLang, lang, setLang]);
  return <Outlet />;
};

const HomeWithData = () => (<><SEO pathKey="/" /><HomePage /></>);

// Legacy Wix path -> new route. Language inferred from the old subdomain
// (it.ilakeitbellagio.it = IT, www.ilakeitbellagio.it = EN) when present.
const LegacyWix = ({ to }) => {
  const stored = (typeof window !== "undefined" && localStorage.getItem("ilakeit_lang")) || "it";
  let lang = SUPPORTED.includes(stored) ? stored : "it";
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (host.startsWith("it.")) lang = "it";
    else if (host.startsWith("www.")) lang = "en";
  }
  return <Navigate to={`/${lang}${to}`} replace />;
};

const W = (Comp, pathKey) => () => (<><SEO pathKey={pathKey} /><Comp /></>);

const DefaultRedirect = () => {
  const stored = (typeof window !== "undefined" && localStorage.getItem("ilakeit_lang")) || "it";
  let lang = SUPPORTED.includes(stored) ? stored : "it";
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (host.startsWith("it.")) lang = "it";
    else if (host.startsWith("www.")) lang = "en";
  }
  return <Navigate to={`/${lang}`} replace />;
};

const LangCatchAll = () => {
  const { lang } = useParams();
  const l = SUPPORTED.includes(lang) ? lang : "it";
  return <Navigate to={`/${l}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <Analytics />
        <BrowserRouter>
          <ScrollToTop />
          <HostRedirect />
          <div className="App">
            <Navbar />
            <main>
              <Routes>
                <Route path="/" element={<DefaultRedirect />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/:lang" element={<LangSync />}>
                  <Route index element={<HomeWithData />} />
                  <Route path="bellagio" element={W(BellagioPage, "/bellagio")()} />
                  <Route path="camere" element={W(RoomsPage, "/camere")()} />
                  <Route path="colazione" element={W(BreakfastPage, "/colazione")()} />
                  <Route path="chi-siamo" element={W(AboutPage, "/chi-siamo")()} />
                  <Route path="come-arrivare" element={W(LocationPage, "/come-arrivare")()} />
                  <Route path="recensioni" element={W(ReviewsPage, "/recensioni")()} />
                  <Route path="prenota" element={W(BookingPage, "/prenota")()} />
                  <Route path="servizi-trasporti" element={W(ServicesPage, "/servizi-trasporti")()} />
                  <Route path="blog" element={W(BlogPage, "/blog")()} />
                  <Route path="blog/:slug" element={<BlogPostPage />} />
                  <Route path="privacy" element={<PrivacyPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="*" element={<LangCatchAll />} />
                </Route>
                {/* Legacy Wix URLs (301-style) -> new routes, language by old subdomain */}
                <Route path="/rooms-1" element={<LegacyWix to="/camere" />} />
                <Route path="/copia-di-how-to-get" element={<LegacyWix to="/come-arrivare" />} />
                <Route path="/customerreviews" element={<LegacyWix to="/recensioni" />} />
                <Route path="/booking" element={<LegacyWix to="/prenota" />} />
                <Route path="/bellagio" element={<LegacyWix to="/bellagio" />} />
                {/* Legacy redirects (no lang prefix) */}
                <Route path="/camere" element={<Navigate to="/it/camere" replace />} />
                <Route path="/chi-siamo" element={<Navigate to="/it/chi-siamo" replace />} />
                <Route path="/come-arrivare" element={<Navigate to="/it/come-arrivare" replace />} />
                <Route path="/recensioni" element={<Navigate to="/it/recensioni" replace />} />
                <Route path="/prenota" element={<Navigate to="/it/prenota" replace />} />
                <Route path="/servizi-trasporti" element={<Navigate to="/it/servizi-trasporti" replace />} />
                <Route path="/privacy" element={<Navigate to="/it/privacy" replace />} />
                <Route path="/faq" element={<Navigate to="/it/faq" replace />} />
                <Route path="/blog" element={<Navigate to="/it/blog" replace />} />
              </Routes>
            </main>
            <Footer />
            <FloatingContact />
            <CookieBanner />
            <Toaster />
          </div>
        </BrowserRouter>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;

